import { useAppSelector } from '@/app/redux/hooks';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { ShareLink, ShareLinkType } from '@/common/types/shareLink';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import api from '@/db/apiClient';
import {
  ChartLineIcon,
  CheckCircleIcon,
  CheckIcon,
  PulseIcon,
} from '@phosphor-icons/react';
import { Alert, AlertDescription, AlertTitle } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { Input } from '@shadcn/components/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import dayjs from 'dayjs';
import { AlertCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

const REGENERATE_COOLDOWN_SECS = 10;

interface ShareLinkDialogueProps {
  open: boolean;
  onClose: () => void;
  studentId: string;
  type: ShareLinkType;
  resourceId: string;
}
export default function ShareLinkDialogue({
  open,
  onClose,
  studentId,
  type,
  resourceId,
}: ShareLinkDialogueProps) {
  const { tab } = useParams();

  const { currentOrganisationId } = useCurrentOrganisationId();
  const { studentsById } = useStudents();

  const userId = useAppSelector(selectUserId);

  const [isLoading, setLoading] = useState(true);
  const [isRegenerating, setRegenerating] = useState(false);
  const [shareLink, setShareLink] = useState<ShareLink>();
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false);
  const [regenerateSuccess, setRegenerateSuccess] = useState<boolean | null>(
    null,
  );
  const [regenerateCooldown, setRegenerateCooldown] = useState<number | null>(
    null,
  );

  const cooldownIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    const interval = cooldownIntervalRef.current;
    return () => {
      if (interval !== null) {
        clearInterval(interval);
      }
    };
  }, []);

  const student = useMemo(() => {
    return studentsById.get(studentId);
  }, [studentsById, studentId]);

  const linkText = useMemo(() => {
    if (!shareLink) return '';
    let url = `${import.meta.env.VITE_APP_BASE_URL}/share/${shareLink.type}/${
      shareLink.id
    }`;
    if (tab && tab !== 'activity') url += `/${tab}`;
    return url;
  }, [shareLink, tab]);

  const handleClose = useCallback(() => {
    setShowCopiedFeedback(false);
    setRegenerateSuccess(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    (async () => {
      if (!open) return;
      setLoading(true);
      setRegenerateSuccess(null);
      if (!currentOrganisationId) {
        console.error('Missing org ID');
        toast.error('Failed to get share link', {
          description: UNKNOWN_ERROR_MESSAGE,
        });
        return;
      }
      try {
        const link = await api.asEducator.getOrCreateShareLink(
          currentOrganisationId,
          studentId,
          type,
          resourceId,
        );
        setShareLink(link);

        trackMixpanelEvent('Share Link Retrieved', {
          studentId,
          type,
          resourceId,
        });
      } catch (e) {
        console.error('Error getting or creating share link:', e);
        toast.error('Failed to get share link', {
          description: UNKNOWN_ERROR_MESSAGE,
        });
        handleClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [
    open,
    type,
    resourceId,
    handleClose,
    studentId,
    userId,
    currentOrganisationId,
  ]);

  async function handleCopy() {
    await navigator.clipboard.writeText(linkText);
    setShowCopiedFeedback(true);
    setTimeout(() => setShowCopiedFeedback(false), 3000);

    trackMixpanelEvent('Share Link Copied', { link: linkText });
  }

  const handleRegenerateLink = useCallback(async () => {
    if (!shareLink) return;

    setShowCopiedFeedback(false);
    setRegenerateSuccess(null);

    try {
      setRegenerating(true);

      // Delete old link
      await api.asEducator.deleteShareLink(shareLink.id);

      // Create new one
      const newShareLink = await api.asEducator.createShareLink(
        shareLink.organisationId,
        shareLink.studentId,
        shareLink.type,
        shareLink.resourceId,
      );

      // Give some time to make it look like something's happening
      setShareLink(undefined);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setShareLink(newShareLink);
      setRegenerateSuccess(true);

      trackMixpanelEvent('Share Link Regenerated', {
        studentId: newShareLink.studentId,
        type: newShareLink.type,
        resourceId: newShareLink.resourceId,
      });

      // Start / restart cooldown
      if (cooldownIntervalRef.current !== null) {
        clearInterval(cooldownIntervalRef.current);
      }

      // Immediately set full cooldown value
      setRegenerateCooldown(REGENERATE_COOLDOWN_SECS);

      const intervalId = window.setInterval(() => {
        setRegenerateCooldown((prev) => {
          if (!prev) return prev;
          if (prev <= 1) {
            // Countdown finished: clear interval and set to "ready" (0)
            clearInterval(intervalId);
            cooldownIntervalRef.current = null;
            return null;
          }
          return prev - 1;
        });
      }, 1000);

      cooldownIntervalRef.current = intervalId;
    } catch (e) {
      console.error('Error regenerating share link:', e);
      setRegenerateSuccess(false);
    } finally {
      setRegenerating(false);
    }
  }, [shareLink]);

  if (!student) return null;
  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {type === 'student' ? (
              <span>
                Share {student.studentFirstName} {student.studentSurname}'s
                activity and insights
              </span>
            ) : type === 'session' ? (
              <span>
                Share {student.studentFirstName} {student.studentSurname}'s
                Spellcaster session
              </span>
            ) : (
              <span>
                Share {student.studentFirstName} {student.studentSurname}'s
                activity result
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {type === 'student' ? (
              <span>
                Use this link to share this learner's{' '}
                <span className="font-semibold">Activity</span> and{' '}
                <span className="font-semibold">Insights</span> data with
                members outside your organisation.
              </span>
            ) : type === 'session' ? (
              <span>
                Use this link to share this learner's session of Spellcaster
                along with their other{' '}
                <span className="font-semibold">Activity</span> and{' '}
                <span className="font-semibold">Insights</span> data with
                members outside your organisation.
              </span>
            ) : (
              <span>
                Use this link to share this learner's activity result along with
                their other <span className="font-semibold">Activity</span> and{' '}
                <span className="font-semibold">Insights</span> data with
                members outside your organisation.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-6">
          {regenerateSuccess ? (
            <Alert className="text-rag-green border-rag-green -mb-2">
              <CheckCircleIcon
                color="hsl(var(--rag-green))"
                className="h-4 w-4 ml-2"
              />
              <AlertDescription>Link regenerated</AlertDescription>
            </Alert>
          ) : regenerateSuccess === false ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4 ml-2" />
              <AlertTitle>Failed to regenerate link</AlertTitle>
              <AlertDescription>{UNKNOWN_ERROR_MESSAGE}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex items-center gap-2">
            <Input
              value={linkText}
              placeholder={
                isRegenerating ? 'Regenerating link...' : 'Loading...'
              }
              readOnly
            />

            <Tooltip open={showCopiedFeedback}>
              <TooltipTrigger asChild>
                <Button
                  onClick={handleCopy}
                  loading={isLoading}
                  disabled={isRegenerating}
                >
                  Copy
                </Button>
              </TooltipTrigger>

              <TooltipContent
                className="bg-white border-rag-green border text-rag-green flex items-center gap-1 p-1"
                side="top"
                sideOffset={6}
              >
                <CheckIcon />
                Copied
              </TooltipContent>
            </Tooltip>
          </div>

          <div className="flex gap-4 text-xs">
            <div className="flex flex-col gap-1 w-1/2">
              <span>People with the link will be able to:</span>
              <span className="ml-2 flex items-center gap-2">
                <PulseIcon size={16} /> View this learner's Activity Feed
              </span>
              <span className="ml-2 flex items-center gap-2">
                <ChartLineIcon size={16} /> View this learner's Insights Panel
              </span>
            </div>

            <div className="flex flex-col gap-2 w-1/2 items-end">
              {shareLink && (
                <span>
                  Expires: {dayjs(shareLink?.expiresAt).format('DD/MM/YYYY')}
                </span>
              )}
              <Button
                variant="secondary"
                className="w-fit"
                onClick={handleRegenerateLink}
                loading={isRegenerating}
                disabled={isLoading || !!regenerateCooldown}
              >
                {regenerateCooldown && (
                  <span className="text-primary font-bold mr-2">
                    {regenerateCooldown}
                  </span>
                )}
                <span>Regenerate Link</span>
              </Button>
            </div>
          </div>
          <span className="text-xs text-muted-foreground -mt-4 text-end">
            For security we recommend periodically regenerating a link you have
            shared
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
